/*
 * A link's preview, made on the device that sends it.
 *
 * A browser tab and the Android app are not allowed to read another site's
 * page, so the page is fetched by the deployment's own server
 * (server/relay/lib/preview.js), which learns the address being shared — and
 * the address of whoever asked — not who it is going to, or anything else in the
 * chat. The picture is shrunk here to a small JPEG, and the whole card travels
 * inside the message, so whoever receives it fetches nothing at all.
 *
 * Off with the "Link previews" switch in Settings, and never for Pine's own
 * links, which get a card of their own made from the link itself.
 */

import { shrinkToJpeg } from './media-prep.js'

const URL_RE = /\bhttps?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/i
const TIMEOUT = 5000
const IMAGE_BUDGET = 24 * 1024

/** The first link in a text worth a preview, or ''. */
export function previewableUrl (text) {
  const match = String(text || '').match(URL_RE)
  if (!match) return ''
  const url = match[0]
  // Pine's own invite and collectible links are shown from the link itself.
  if (/\/(?:i|c)\/[a-z0-9]{16,}/i.test(url) || /^pine:/i.test(url)) return ''
  return url
}

/** @returns { url, title, description, site, image } (image as base64 JPEG) or null */
export async function fetchPreview (base, url) {
  if (!base || !url) return null
  const root = String(base).replace(/\/+$/, '')
  const page = await get(root + '/api/preview?url=' + encodeURIComponent(url), 'json')
  if (!page || !(page.title || page.description)) return null

  const out = {
    url,
    title: String(page.title || '').slice(0, 200),
    description: String(page.description || '').slice(0, 300),
    site: String(page.site || '').slice(0, 80),
    image: ''
  }
  if (page.image) {
    const bytes = await get(root + '/api/preview/image?url=' + encodeURIComponent(page.image), 'bytes')
    if (bytes) out.image = await shrink(bytes)
  }
  return out
}

async function shrink (bytes) {
  for (const side of [360, 240]) {
    const jpeg = await shrinkToJpeg(bytes, { side, quality: 0.65 })
    if (jpeg && jpeg.byteLength <= IMAGE_BUDGET) return toBase64(jpeg)
  }
  return ''
}

function toBase64 (bytes) {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000))
  }
  return btoa(binary)
}

async function get (url, as) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT)
  try {
    const response = await fetch(url, { signal: controller.signal, cache: 'no-store' })
    if (!response.ok) return null
    return as === 'json' ? await response.json() : new Uint8Array(await response.arrayBuffer())
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
